import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Download, Plus, Upload, Zap } from 'lucide-react';
import { AppShell } from '../../components/AppShell';
import { Card } from '../../components/ui/Card';
import { Pill } from '../../components/ui/Pill';
import { BankAccountCreateDrawer } from '../../components/BankAccountCreateDrawer';
import {
  useBankAccounts,
  useBankTransactions,
  useImportJpBankStatement,
  useImportUniversalCsv,
  useAutoMatch,
  useMatchSuggestions,
  type JpBank,
} from '../../hooks/useBankFeed';
import { downloadBankTemplate } from '../../lib/bank-csv-templates';
import { formatAmount, formatDate } from '../../lib/format';

const BANK_LABELS: Record<JpBank, string> = {
  mizuho: 'みずほ銀行 (Shift_JIS)',
  smbc: '三井住友銀行',
  mufg: '三菱UFJ銀行 (Shift_JIS)',
  rakuten: '楽天銀行',
  paypay: 'PayPay 銀行',
  universal: 'Universal CSV',
};

export function BankFeedPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t, i18n } = useTranslation();
  const [selectedAccount, setSelectedAccount] = useState<string>('');
  const [bank, setBank] = useState<JpBank>('mizuho');
  const [file, setFile] = useState<File | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [suggestFor, setSuggestFor] = useState<string | null>(null);

  const accounts = useBankAccounts(wsId);
  const txs = useBankTransactions(wsId, selectedAccount || undefined);
  const importJp = useImportJpBankStatement(wsId);
  const importUniversal = useImportUniversalCsv(wsId);
  const autoMatch = useAutoMatch(wsId);

  const acc = accounts.data?.find((a) => a.id === selectedAccount) ?? accounts.data?.[0];

  const onImport = async () => {
    setError(null);
    setInfo(null);
    if (!acc) {
      setError(t('bankFeed.errorPickAccount'));
      return;
    }
    if (!file) {
      setError(t('bankFeed.errorPickFile'));
      return;
    }
    try {
      if (bank === 'universal') {
        // Read the file in the browser — universal endpoint takes plain CSV text.
        const csv = await file.text();
        const r = await importUniversal.mutateAsync({ bankAccountId: acc.id, csv, currency: acc.currency });
        setInfo(t('bankFeed.imported', { count: r.imported }));
      } else {
        const r = await importJp.mutateAsync({ bank, bankAccountId: acc.id, currency: acc.currency, file });
        setInfo(t('bankFeed.imported', { count: r.imported }));
      }
      setFile(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Import failed');
    }
  };

  const hasAccounts = (accounts.data ?? []).length > 0;

  return (
    <AppShell>
      <div className="px-8 py-7 max-w-7xl">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="display text-2xl font-semibold">{t('bankFeed.title')}</h1>
            <p className="text-sm text-ink-500 mt-1">{t('bankFeed.subtitle')}</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => autoMatch.mutate()}
              disabled={autoMatch.isPending || !hasAccounts}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900 text-sm font-medium disabled:opacity-40"
            >
              <Zap size={14} />
              {t('bankFeed.autoMatch')}
            </button>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-foreground text-background hover:bg-foreground/90 text-sm font-medium"
            >
              <Plus size={14} />
              {t('bankFeed.addAccount')}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-5">
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">
                {t('bankFeed.accounts')}
              </div>
              {accounts.isLoading ? (
                <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
              ) : !hasAccounts ? (
                <div className="p-10 text-center text-ink-500 text-sm">{t('bankFeed.noAccounts')}</div>
              ) : (
                <ul className="divide-y divide-ink-200 dark:divide-ink-800">
                  {(accounts.data ?? []).map((a) => (
                    <li
                      key={a.id}
                      className={`px-5 py-3 cursor-pointer text-sm ${
                        (selectedAccount || accounts.data?.[0]?.id) === a.id
                          ? 'bg-accent-50 dark:bg-accent-900/30'
                          : ''
                      }`}
                      onClick={() => setSelectedAccount(a.id)}
                    >
                      <div className="font-medium">{a.display_name}</div>
                      <div className="text-xs text-ink-500">
                        {a.bank_code ?? '—'}-{a.branch_code ?? '—'} · {a.currency}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-5">
              <div className="font-medium mb-1">{t('bankFeed.importStatement')}</div>
              <p className="text-xs text-ink-500 mb-4">{t('bankFeed.importHelp')}</p>

              <label className="text-[11px] uppercase tracking-wide text-ink-500">
                {t('bankFeed.bank')}
              </label>
              <select
                value={bank}
                onChange={(e) => setBank(e.target.value as JpBank)}
                className="mt-1 mb-3 w-full px-2 py-1.5 text-sm rounded-md border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 focus:outline-none focus:ring-2 focus:ring-accent-500"
              >
                {(Object.keys(BANK_LABELS) as JpBank[]).map((b) => (
                  <option key={b} value={b}>
                    {BANK_LABELS[b]}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={() => downloadBankTemplate(bank)}
                className="inline-flex items-center gap-1.5 text-xs text-accent-600 hover:text-accent-700 mb-3"
              >
                <Download size={12} />
                {t('bankFeed.downloadSample')} — {BANK_LABELS[bank]}
              </button>

              <label className="text-[11px] uppercase tracking-wide text-ink-500 block mb-1">
                {t('bankFeed.statementFile')}
              </label>
              <input
                type="file"
                accept=".csv,text/csv,text/plain"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                className="w-full text-xs"
              />
              {file && (
                <div className="text-xs text-ink-500 mt-1 truncate">
                  {file.name} · {(file.size / 1024).toFixed(1)} KB
                </div>
              )}

              {error && (
                <div className="mt-3 text-xs text-warn-600 rounded-md border border-warn-300 bg-warn-50 dark:bg-warn-900/20 px-2 py-1.5">
                  {error}
                </div>
              )}
              {info && (
                <div className="mt-3 text-xs text-emerald-700 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1.5">
                  {info}
                </div>
              )}

              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={onImport}
                  disabled={importJp.isPending || importUniversal.isPending || !hasAccounts || !file}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-600 text-white text-xs font-medium hover:bg-accent-700 disabled:opacity-50"
                >
                  <Upload size={12} />
                  {t('bankFeed.import')}
                </button>
              </div>

              {autoMatch.data && (
                <div className="mt-3 text-xs text-emerald-700">
                  {t('bankFeed.matched', { count: (autoMatch.data as { matched: number }).matched })}
                </div>
              )}
            </Card>
          </div>

          <div className="lg:col-span-2">
            <Card>
              <div className="px-5 py-3 border-b border-ink-200 dark:border-ink-800 font-medium">
                {t('bankFeed.transactions')}
              </div>
              {txs.isLoading ? (
                <div className="p-10 text-center text-ink-500">{t('common.loading')}</div>
              ) : !txs.data || txs.data.length === 0 ? (
                <div className="p-10 text-center text-ink-500 text-sm">{t('bankFeed.noTransactions')}</div>
              ) : (
                <ul className="divide-y divide-ink-200 dark:divide-ink-800">
                  {txs.data.map((tx) => (
                    <li key={tx.id} className="px-5 py-3 text-sm">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <div className="font-medium truncate">
                            {tx.counterparty ?? tx.description ?? '—'}
                          </div>
                          <div className="text-xs text-ink-500">
                            {formatDate(tx.posted_on, i18n.language)}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          {tx.matched_kind ? (
                            <Pill tone="success">{tx.matched_kind}</Pill>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setSuggestFor(suggestFor === tx.id ? null : tx.id)}
                              className="text-xs px-2 py-1 rounded-md border border-ink-300 dark:border-ink-700 hover:bg-ink-50 dark:hover:bg-ink-900"
                              aria-expanded={suggestFor === tx.id}
                            >
                              {t('bankFeed.suggest')}
                            </button>
                          )}
                          <span className="num font-medium">
                            {formatAmount(tx.amount_cents, tx.currency, i18n.language)}
                          </span>
                        </div>
                      </div>
                      {suggestFor === tx.id && wsId && <TxSuggestions wsId={wsId} txId={tx.id} />}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </div>
        </div>
      </div>

      {wsId && (
        <BankAccountCreateDrawer
          wsId={wsId}
          open={createOpen}
          onOpenChange={setCreateOpen}
          onCreated={(id) => setSelectedAccount(id)}
        />
      )}
    </AppShell>
  );
}

/** Inline suggested-match list for one unmatched transaction (F8). Runs its
 *  own query only while expanded. */
function TxSuggestions({ wsId, txId }: { wsId: string; txId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useMatchSuggestions(wsId, txId);

  if (isLoading) {
    return <div className="mt-2 pl-1 text-xs text-ink-500">{t('common.loading')}</div>;
  }
  if (!data || data.length === 0) {
    return <div className="mt-2 pl-1 text-xs text-ink-500">{t('bankFeed.noSuggestions')}</div>;
  }
  return (
    <ul className="mt-2 pl-1 space-y-1">
      {data.map((s) => (
        <li key={s.id} className="flex items-center gap-2 text-xs">
          <Pill tone="accent">{t(`bankFeed.kind.${s.matchedKind}`)}</Pill>
          <span className="truncate text-ink-600 dark:text-ink-300">{s.partyName ?? '—'}</span>
          {s.firstTimePartner && <Pill tone="warn">{t('bankFeed.firstTimePartner')}</Pill>}
          <span className="num ml-auto text-ink-500">{Math.round(s.score * 100)}%</span>
        </li>
      ))}
    </ul>
  );
}
